import { AuditFeed } from "@playbookai/ui";
import { useKpiAudit } from "../hooks/useKpiAudit.js";

/**
 * KPI governance activity. This intentionally sits outside Performance:
 * measurements answer "what value changed?", while audit answers "who changed
 * the KPI's governed setup or lifecycle?". The feed draws its own "Activity"
 * heading, so the card frame here carries no second one (d37).
 */
export function KpiActivitySection({
  kpiId,
}: {
  readonly kpiId: string;
}): JSX.Element {
  const query = useKpiAudit(kpiId);
  /* `pages.length`, NOT `entries.length`: the client filters internal receipts,
     so a receipt-only page contributes no rows while still being a page that
     loaded — reading emptiness as "nothing loaded" would show the error panel
     over a healthy feed. */
  const hasLoadedPage = (query.data?.pages.length ?? 0) > 0;
  return (
    // Kept hand-built (not Card): Card's fixed fill opacities don't
    // reproduce this translucent bg-pb-card/45 — see FormSection.tsx.
    <section
      aria-label="Activity"
      className="h-112 overflow-hidden rounded-xl border border-pb-border/80 bg-pb-card/45"
    >
      <AuditFeed
        /* `AuditFeed.entries` is a mutable array and the hook hands back a
           readonly one — the same spread the four History rails do. */
        entries={[...query.entries]}
        label="Activity"
        subtitle="Recent governance changes on this KPI."
        isLoading={query.isPending}
        error={
          query.isError && !hasLoadedPage
            ? "Activity is unavailable for this KPI."
            : null
        }
        /* Non-blocking, unlike `error`: every row is here and readable, only
           some of the NAMES are missing. Without it an unresolved actor's
           "A member" cannot be told apart from someone who has left the
           tenant. `AuditFeed` suppresses it while loading or erroring. */
        notice={
          query.actorNamesPartial
            ? "Some names could not be loaded, so those entries show “A member”."
            : null
        }
        hasMore={query.hasNextPage}
        /* `isFetching`, not just `isFetchingNextPage`: with polling on, a
           background refetch also blocks the click below, and a button that
           looks enabled but does nothing reads as broken. This keeps the
           existing aria-disabled state honest during a poll. */
        isLoadingMore={query.isFetching}
        onLoadMore={() => {
          if (!query.isFetching) void query.fetchNextPage();
        }}
      />
    </section>
  );
}
